/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */

import {GenericObject} from '@ion/core/src/types/entities';
import {
  EntitySubscription,
  ObservableEntityId,
  ObserverMap,
  OnDataEvent
} from './types';

/**
 * The Observers API manages the state of all WebSocket subscriptions based on the
 * id of the entity being observed. Note, this API is primarily only intended to
 * be used internally by the lower-level EntityObserver API.
 */
export default class Observers {
  /**
   * Defines the mapping of observed ids to collections of OnDataEvent function
   * associations which will be invoked upon receiving a message for the given
   * id.
   */
  private observers: ObserverMap = {
    [EntitySubscription.asset] : {},
    [EntitySubscription.task]  : {}

  };

  /**
   * Adds an observer for the given id (or collection of ids) for the provided
   * observer function. When a message is received which matches the specified
   * id, the observer will be invoked with the change.
   *
   * @param entity The type of entity for which the observer is to be added.
   * @param ids Either a single id, or array of ids to be observed for changes.
   * @param onData Callback function which will be invoked upon receiving changes
   *        for the given ids.
   */
  addObserver = (entity: EntitySubscription, ids: ObservableEntityId[], onData: OnDataEvent) => {
    const entities = this.observers[entity];
    ids?.forEach((id: ObservableEntityId) => {
      const observers = entities[id];
      if (!observers?.length) {
        entities[id] = [onData];
      } else {
        observers.push(onData);
      }
    });
  };

  /**
   * Removes a previously added observer for the given id (or collection of ids).
   * If the provided id(s) no longer have any observers associated with them, an
   * object containing the obsolete ids will be returned.
   *
   * @param   entity The type of entity for which the observer is to be removed.
   * @param   ids Either a single id, or array of ids which are no longer to be
   *          observed for changes.
   * @param   onData The callback function previously passed to `addObserver` for
   *          the given id(s).
   * @returns An object containing the `ids` (as keys) for all entities which are
   *          no longer being observed and are to be removed (if any), otherwise,
   *          undefined.
   */
  removeObserver = (entity: EntitySubscription, ids: ObservableEntityId[], onData?: OnDataEvent): GenericObject => {
    const entities = this.observers[entity];
    const obsolete: GenericObject = {};
    ids?.forEach((id: ObservableEntityId) => {
      const observers = entities[id];
      if (observers?.length) {
        entities[id] = observers.filter(registered => registered !== onData);
        if (!entities[id].length || !onData) {
          obsolete[id] = true;
          delete entities[id];
        }
      }
    });
    return obsolete;
  };

  /**
   * Invokes all currently registered observers for the given entity id.
   */
  invokeObservers = (id: ObservableEntityId, change: GenericObject): void => {
    Object.entries(this.observers).find(([entity, mappings]) => {
      const observers = mappings[id];
      if (observers?.length) {
        observers.forEach(observer => {
          observer(change, id);
        });
        return true;
      }
      return false;
    });
  };

  /**
   * Determines if any observers are currently registered for the entity of the
   * given id.
   */
  hasObserver(entity: EntitySubscription, id: ObservableEntityId, observer: OnDataEvent): boolean {
    return !!(this.observers[entity]?.[id]?.find(item => item === observer));
  };

  /**
   * Returns the number of current observers for the entity of the given id.
   */
  getObserverCount(entity: EntitySubscription, id: ObservableEntityId): number {
    return this.observers[entity]?.[id]?.length || 0;
  };

  /**
   * Provides a utility which returns all currently observed entity ids.
   *
   * @returns An array of entity ids currently being observed.
   */
  getObservedIds(entity: EntitySubscription): ObservableEntityId[] {
    return Object.keys(this.observers[entity]);
  }

  /**
   * Provides a utility which returns a collection of obsolete entity ids; i.e.
   * ids which are no longer being observed.
   *
   * @param   obsolete An object containing the ids of entities which are no
   *          longer being observed.
   * @returns An array of obsolete entity ids (if any), otherwise, an empty
   *          array is returned.
   */
  static getObsoleteIds(obsolete: GenericObject): ObservableEntityId[] {
    return Object.keys(obsolete);
  }
}
